/* global grecaptcha, window, document */
import {
  injectScript,
  waitForScriptToLoad,
  removeDomElement,
} from 'app/utils/env';
import appConfig from 'app/config/client';
import TaskQueue from 'app/utils/queues/TaskQueue';

let recaptchaScriptPromise: Promise<void> | undefined = undefined;

const ONLOAD_CALLBACK_FUNCTION_NAME = 'recaptchaIntegration__onloadCallback';
const TOKEN_CALLBACK_FUNCTION_NAME = 'recaptchaIntegration__tokenCallback';

/**
 * Loads and initializes the reCAPTCHA code, but does not generate a token. The
 * reCAPTCHA code is loaded once, and then cached so all future calls to
* `ensureRecaptchaLoaded()` will resolve immediately.
 */
export const ensureRecaptchaLoaded = async () => {
  // Only load recaptcha script once.
  if (!recaptchaScriptPromise) {
    recaptchaScriptPromise = loadRecaptchaScript();
  }

  try {
    await recaptchaScriptPromise;
  } catch (error) {
    // If there is an error while trying to load recaptcha, reset the state so
    // we can retry loading next time someone calls ensureRecaptchaLoaded().
    recaptchaScriptPromise = undefined;
    throw error;
  }
};

async function loadRecaptchaScript() {
  const onLoadPromise = windowCallbackPromise<void>(ONLOAD_CALLBACK_FUNCTION_NAME);

  // `render=explicit` tells recaptcha to not render anything until we
  // explicitly call `grecaptcha.render`.
  //
  // `onload` will be called when the library is ready.
  const recaptchaScriptNode = injectScript(
    `https://www.google.com/recaptcha/api.js?render=explicit` +
      `&onload=${ONLOAD_CALLBACK_FUNCTION_NAME}`,
  );

  try {
    await waitForScriptToLoad(recaptchaScriptNode);

    await onLoadPromise;

    // Render a global, invisible recaptcha element
    //
    // Pages that actually use recaptcha should render a "this site uses
    // recpatcha" legal message themselves.
    const invisibleRecaptchaContainer = document.createElement('div');
    invisibleRecaptchaContainer.style.visibility = 'hidden'; // Make hidden
    invisibleRecaptchaContainer.style.position = 'fixed'; // Don't affect layout
    document.body.appendChild(invisibleRecaptchaContainer);

    try {
      grecaptcha.render(invisibleRecaptchaContainer, {
        sitekey: appConfig.recaptcha.siteKey,
        size: 'invisible',
        badge: 'inline',
        callback: TOKEN_CALLBACK_FUNCTION_NAME,
      });
    } catch (error) {
      removeDomElement(invisibleRecaptchaContainer);

      throw error;
    }
  } catch (error) {
    removeDomElement(recaptchaScriptNode);

    throw error;
  }
}

const getRecaptchaTokenQueue = new TaskQueue();

/**
 * Loads reCAPTCHA if it has not already been loaded, and generates and returns
 * a reCAPTCHA token. Does not require any UI interaction from the user, and
 * does not show any "badge" on the page.
 */
export const getRecaptchaToken = (): Promise<string> => {
  // Use a task queue just to make sure there are no race conditions caused by
  // multiple places calling getRecaptchaToken()/etc. at the same time.
  return getRecaptchaTokenQueue.push(async () => {
    await ensureRecaptchaLoaded();

    const onTokenPromise = windowCallbackPromise<string>(TOKEN_CALLBACK_FUNCTION_NAME);

    grecaptcha.execute();

    const token = await onTokenPromise;

    // We need to reset the reCAPTCHA widget in case it is necessary to call grecaptcha.execute()
    // multiple times.
    grecaptcha.reset();

    return token;
  });
};

/**
 * reCAPTCHA v2 requires us to use global callback functions attached to
 * `window`. This requires us to ignore lots of types errors, which we hide
 * inside this helper.
 */
function windowCallbackPromise<T>(callbackName: string): Promise<T> {
  return new Promise<T>(resolve => {
    // @ts-ignore
    const originalValue: unknown = window[callbackName];

    // @ts-ignore
    window[callbackName] = (value: any) => {
      // @ts-ignore
      window[callbackName] = originalValue;

      resolve(value);
    };
  });
}
